import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const TaskCreatePage = () => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const isEdit = !!taskId;

  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'inspection',
    service_routine: '',
    client_id: '',
    site_id: '',
    assets: [],
    priority: 'normal',
    scheduled_date: '',
    scheduled_time_slot: '',
    assigned_technician_id: '',
    estimated_duration: 120
  });

  const taskTypes = ['inspection', 'maintenance', 'repair', 'installation'];
  const serviceRoutines = [
    'Fire Extinguisher',
    'Fire Hose Reel',
    'Fire Hydrant',
    'Fire Blanket',
    'Exit & Emergency Lighting',
    'Fire Doors',
    'Sprinkler Systems',
    'Fire Alarm Systems'
  ];
  const priorities = ['low', 'normal', 'high', 'urgent'];
  const timeSlots = ['morning', 'afternoon', 'full_day'];

  useEffect(() => {
    fetchClients();
    fetchTechnicians();
    if (isEdit) {
      fetchTask();
    }
  }, [taskId]);

  useEffect(() => {
    if (formData.client_id && formData.site_id) {
      fetchAssets();
    }
  }, [formData.client_id, formData.site_id]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients', error);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/technicians`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTechnicians(response.data.filter(t => t.status === 'active'));
    } catch (error) {
      console.error('Failed to load technicians', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assets?client_id=${formData.client_id}&site_id=${formData.site_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets', error);
    }
  };

  const fetchTask = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const task = response.data.find(t => t.id === taskId);
      if (task) {
        setFormData({
          title: task.title,
          description: task.description || '',
          task_type: task.task_type,
          service_routine: task.service_routine,
          client_id: task.client_id,
          site_id: task.site_id,
          assets: task.assets || [],
          priority: task.priority,
          scheduled_date: task.scheduled_date || '',
          scheduled_time_slot: task.scheduled_time_slot || '',
          assigned_technician_id: task.assigned_technician_id || '',
          estimated_duration: task.estimated_duration || 120
        });
      }
    } catch (error) {
      toast.error('Failed to load task');
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setFormData({ ...formData, client_id: clientId, site_id: '', assets: [] });
  };

  const addAsset = (asset) => {
    if (!formData.assets.find(a => a.asset_id === asset.id)) {
      setFormData({
        ...formData,
        assets: [...formData.assets, {
          asset_id: asset.id,
          asset_tag: asset.asset_tag,
          asset_type: asset.asset_type,
          location: asset.location
        }]
      });
    }
  };

  const removeAsset = (assetId) => {
    setFormData({
      ...formData,
      assets: formData.assets.filter(a => a.asset_id !== assetId)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.client_id || !formData.site_id || !formData.service_routine) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (isEdit) {
        await axios.put(`${API}/tasks/${taskId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Task updated successfully!');
      } else {
        await axios.post(`${API}/tasks`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Task created successfully!');
      }
      
      navigate('/admin/tasks');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  return (
    <AdminLayout>
      <Toaster />
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/tasks')}
            className="text-gray-400 hover:text-white hover:bg-gray-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">{isEdit ? 'Edit Task' : 'Create New Task'}</h1>
            <p className="text-gray-400 mt-2">Fill in the details below to {isEdit ? 'update' : 'create'} a task</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Task Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white"
                      placeholder="e.g., Monthly Fire Extinguisher Inspection"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="task_type">Task Type *</Label>
                      <Select value={formData.task_type} onValueChange={(value) => setFormData({ ...formData, task_type: value })}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          {taskTypes.map(type => (
                            <SelectItem key={type} value={type} className="text-white">
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service_routine">Service Routine *</Label>
                      <Select value={formData.service_routine} onValueChange={(value) => setFormData({ ...formData, service_routine: value })}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          {serviceRoutines.map(routine => (
                            <SelectItem key={routine} value={routine} className="text-white">
                              {routine}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Location & Assets</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="client">Client *</Label>
                    <Select value={formData.client_id} onValueChange={handleClientChange}>
                      <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id} className="text-white">
                            {client.client_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedClient && (
                    <div className="space-y-2">
                      <Label htmlFor="site">Site *</Label>
                      <Select value={formData.site_id} onValueChange={(value) => setFormData({ ...formData, site_id: value, assets: [] })}>
                        <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                          <SelectValue placeholder="Select site" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          {selectedClient.sites?.map(site => (
                            <SelectItem key={site.site_name} value={site.site_name} className="text-white">
                              {site.site_name} - {site.site_address}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {availableAssets.length > 0 && (
                    <div className="space-y-2">
                      <Label>Add Assets</Label>
                      <div className="max-h-40 overflow-y-auto space-y-2 p-2 bg-gray-900 rounded border border-gray-700">
                        {availableAssets.map(asset => (
                          <div key={asset.id} className="flex items-center justify-between p-2 hover:bg-gray-800 rounded">
                            <span className="text-sm text-white">{asset.asset_tag} - {asset.asset_type}</span>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => addAsset(asset)}
                              disabled={formData.assets.find(a => a.asset_id === asset.id)}
                              className="bg-orange-600 hover:bg-orange-700"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.assets.length > 0 && (
                    <div className="space-y-2">
                      <Label>Selected Assets ({formData.assets.length})</Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.assets.map(asset => (
                          <Badge key={asset.asset_id} className="bg-orange-600 text-white flex items-center gap-1">
                            {asset.asset_tag}
                            <X className="w-3 h-3 cursor-pointer" onClick={() => removeAsset(asset.asset_id)} />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Scheduling</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                      <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        {priorities.map(priority => (
                          <SelectItem key={priority} value={priority} className="text-white">
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="scheduled_date">Scheduled Date</Label>
                    <Input
                      id="scheduled_date"
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="time_slot">Time Slot</Label>
                    <Select value={formData.scheduled_time_slot} onValueChange={(value) => setFormData({ ...formData, scheduled_time_slot: value })}>
                      <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        {timeSlots.map(slot => (
                          <SelectItem key={slot} value={slot} className="text-white">
                            {slot.replace('_', ' ').charAt(0).toUpperCase() + slot.replace('_', ' ').slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="technician">Assign Technician</Label>
                    <Select value={formData.assigned_technician_id} onValueChange={(value) => setFormData({ ...formData, assigned_technician_id: value })}>
                      <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                        <SelectValue placeholder="Select technician" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        {technicians.map(tech => (
                          <SelectItem key={tech.id} value={tech.id} className="text-white">
                            {tech.first_name} {tech.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration">Estimated Duration (minutes)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.estimated_duration}
                      onChange={(e) => setFormData({ ...formData, estimated_duration: parseInt(e.target.value) })}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-col gap-2">
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700 w-full">
                  <Save className="w-4 h-4 mr-2" />
                  {isEdit ? 'Update Task' : 'Create Task'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/admin/tasks')} className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700 w-full">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default TaskCreatePage;
